import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertActionLink,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  MenuToggle,
  MenuToggleElement,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import {
  CalculatorIcon,
  ExclamationCircleIcon,
  PencilAltIcon,
  PlusCircleIcon,
  SyncAltIcon,
  TrashIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useImpersonation } from '../../../../contexts/impersonation/ImpersonationContext';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useGpuBudgets } from '../../../../hooks/useGpuBudgets';
import { useGpuBudget } from '../../../../hooks/useGpuBudget';
import { useGpuBudgetCatalog } from '../../../../hooks/useGpuBudgetCatalog';
import { useGpuBudgetMutations } from '../../../../hooks/useGpuBudgetMutations';
import type { GpuBudgetWriteError } from '../../../../hooks/gpuBudgetResponse';
import { GPU_BUDGET_MAYBE_SAVED_HINT } from '../../../../hooks/gpuBudgetResponse';
import type {
  GpuBudget,
  GpuBudgetCatalogKind,
  GpuBudgetLine,
  GpuBudgetListItem,
} from '../../../../types/models/GpuBudget';
import { formatWallClockDateTime } from '../../../../utils/dateFormat';
import { catalogKind, formatCount, formatQuantity, lineName } from './budgetLineKinds';
import {
  BUDGET_EMPTY_BODY,
  BUDGET_EMPTY_TITLE,
  BUDGET_ESTIMATE_NOTE,
  BUDGET_IMPERSONATION_NOTICE,
} from './gpuBudgetCopy';
import type { ModalFeedback } from './gpuBudgetFeedback';
import { CLOSE_MODAL } from './gpuBudgetFeedback';
import GpuBudgetConfirmModal from './GpuBudgetConfirmModal';
import GpuBudgetEditor, { GpuBudgetWarningStack } from './GpuBudgetEditor';
import GpuBudgetLineModal from './GpuBudgetLineModal';
import type { GpuBudgetLineSubmit } from './GpuBudgetLineModal';
import GpuBudgetNameModal from './GpuBudgetNameModal';
import { parseBudgetId } from './parseBudgetId';

interface GpuBudgetTabProps {
  /**
   * The Budget tab is the active one. PatternFly keeps a visited tab mounted (mountOnEnter), so the
   * selection is frozen — and never written to the URL — while another tab is shown.
   */
  isActive: boolean;
}

type ModalState =
  | { type: 'create' }
  | { type: 'rename'; budget: GpuBudgetListItem }
  | { type: 'delete'; budget: GpuBudgetListItem }
  | { type: 'line'; budgetId: number; kind: GpuBudgetCatalogKind; line: GpuBudgetLine | null }
  | { type: 'remove-line'; budgetId: number; line: GpuBudgetLine };

type NotificationVariant = 'success' | 'danger' | 'warning';

const SR_TITLES: Record<NotificationVariant, string> = {
  success: 'Success alert',
  danger: 'Error alert',
  warning: 'Warning alert',
};

const toListItem = (budget: GpuBudget): GpuBudgetListItem => ({
  id: budget.id,
  name: budget.name,
  line_count: budget.lines.length,
  created_at: budget.created_at,
  updated_at: budget.updated_at,
});

/** "3 lines · edited 29/09/2026 10:15" under a budget's name in the picker. */
const budgetOptionDescription = (budget: GpuBudgetListItem): string => {
  const lines = `${formatCount(budget.line_count)} line${budget.line_count === 1 ? '' : 's'}`;
  return budget.updated_at !== null ? `${lines} · edited ${formatWallClockDateTime(budget.updated_at)}` : lines;
};

/**
 * GPU Servers › Budget — the organisation's saved, named MONTHLY estimates of GPU servers plus
 * supplementary storage (docs/api-types/gpu-budgets.md), shared by its members.
 *
 * - The list and the catalog load once per mount of this tab (`mountOnEnter` on the page's Tabs); the
 *   selected budget loads on selection. Every write returns the re-priced budget, which replaces the
 *   shown one (no extra GET). Refresh reloads the list, the selected budget and the catalog. Never polled.
 * - Every edit is saved at once, line by line: there is no Save button and no local draft.
 * - Every amount is a backend string, formatted only — nothing is computed here.
 * - Selection lives in `?budget=`: parsed with a digits-only guard and selected ONLY if that id is in
 *   the list; otherwise (absent, malformed, unknown) the first budget, silently. An id that is not in
 *   the list is never fetched; a budget found gone is never fetched again.
 * - Usable under impersonation (a budget moves no money), with an info notice.
 *
 * The budget picker is a plain Select with no search, and the tables have no search and no pagination
 * — an explicit opt-out from the list-table rule (Bruno, 2026-09-29): at most
 * `catalog.limits.max_budgets` budgets and `catalog.limits.max_lines` lines per budget.
 */
function GpuBudgetTab({ isActive }: GpuBudgetTabProps) {
  const { isImpersonating } = useImpersonation();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const list = useGpuBudgets(true);
  const catalogState = useGpuBudgetCatalog(true);
  const mutations = useGpuBudgetMutations();
  const [goneIds, setGoneIds] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [modal, setModal] = useState<ModalState | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const { budgets, refetch: refetchList, upsert } = list;
  const { catalog } = catalogState;

  const notify = useCallback(
    (variant: NotificationVariant, title: string, description: string) => {
      addNotification({ title, srTitle: SR_TITLES[variant], description, variant });
      setDrawerOpen(true);
    },
    [addNotification, setDrawerOpen],
  );

  const markGone = useCallback((id: number) => setGoneIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id))), []);

  // The budgets that can be selected: the list minus the ones found gone (until the list refetch drops them).
  const candidates = useMemo(
    () => (budgets === null ? null : budgets.filter((budget) => !goneIds.has(budget.id))),
    [budgets, goneIds],
  );
  const requestedId = parseBudgetId(searchParams.get('budget'));
  const computedId =
    candidates === null
      ? null
      : requestedId !== null && candidates.some((budget) => budget.id === requestedId)
        ? requestedId
        : (candidates[0]?.id ?? null);
  // While another tab is shown, `budget=` is dropped from the URL: keep the selection frozen meanwhile.
  const [heldId, setHeldId] = useState<number | null>(null);
  if (isActive && computedId !== heldId) {
    setHeldId(computedId);
  }
  const selectedId = isActive ? computedId : heldId;
  const selectedItem = candidates?.find((budget) => budget.id === selectedId) ?? null;

  const handleBudgetNotFound = useCallback(
    (id: number) => {
      markGone(id);
      setModal(null);
      notify('warning', 'Budget not found', 'This budget no longer exists — it may have been deleted by a colleague.');
      void refetchList();
    },
    [markGone, notify, refetchList],
  );

  const budgetState = useGpuBudget(selectedId, handleBudgetNotFound);
  const { budget, setBudget, refetch: refetchBudget } = budgetState;

  // Write the effective selection back to the URL (replace: no history entry per selection).
  useEffect(() => {
    if (!isActive || candidates === null) {
      return;
    }
    const wanted = selectedId === null ? null : String(selectedId);
    if (searchParams.get('budget') === wanted) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (wanted === null) {
      next.delete('budget');
    } else {
      next.set('budget', wanted);
    }
    setSearchParams(next, { replace: true });
  }, [isActive, candidates, selectedId, searchParams, setSearchParams]);

  const selectBudget = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('budget', String(id));
    setSearchParams(next, { replace: true });
  };

  const closeModal = () => setModal(null);

  /**
   * The common handling of a failed write (docs/api-types/gpu-budgets.md "Errors"): always notify, then
   * keep the modal open (field errors, a cap, a refusal) or close it and refetch in the background (a
   * write that may have committed, a budget or line found gone). Never resubmits.
   */
  const handleWriteError = (error: GpuBudgetWriteError, title: string, budgetId: number | null): ModalFeedback => {
    const description =
      error.reason === 'unconfirmed'
        ? `${error.message} ${GPU_BUDGET_MAYBE_SAVED_HINT}`
        : error.reason === 'invalid'
          ? Object.values(error.fieldErrors).join(' ')
          : error.message;
    notify('danger', title, description);
    switch (error.reason) {
      case 'invalid':
        return { close: false, fieldErrors: error.fieldErrors, alert: null };
      case 'limit':
        void refetchList();
        if (budgetId !== null) {
          void refetchBudget();
        }
        return { close: false, fieldErrors: {}, alert: error.message };
      case 'unconfirmed':
        void refetchList();
        if (budgetId !== null) {
          void refetchBudget();
        }
        return CLOSE_MODAL;
      case 'budget_not_found':
        if (budgetId !== null) {
          markGone(budgetId);
        }
        void refetchList();
        return CLOSE_MODAL;
      case 'line_not_found':
        void refetchBudget();
        return CLOSE_MODAL;
      default:
        return { close: false, fieldErrors: {}, alert: error.message };
    }
  };

  const handleCreate = async (name: string): Promise<ModalFeedback> => {
    const outcome = await mutations.createBudget(name);
    if (outcome.status !== 'success') {
      return handleWriteError(outcome, 'Could not create the budget', null);
    }
    upsert(toListItem(outcome.budget));
    setBudget(outcome.budget);
    selectBudget(outcome.budget.id);
    void refetchList();
    notify('success', 'Budget created', `The budget “${outcome.budget.name}” was created.`);
    return CLOSE_MODAL;
  };

  const handleRename = async (target: GpuBudgetListItem, name: string): Promise<ModalFeedback> => {
    const outcome = await mutations.renameBudget(target.id, name);
    if (outcome.status !== 'success') {
      return handleWriteError(outcome, 'Could not rename the budget', target.id);
    }
    upsert(toListItem(outcome.budget));
    setBudget(outcome.budget);
    void refetchList();
    notify('success', 'Budget renamed', `The budget is now called “${outcome.budget.name}”.`);
    return CLOSE_MODAL;
  };

  const handleDelete = async (target: GpuBudgetListItem): Promise<ModalFeedback> => {
    const outcome = await mutations.deleteBudget(target.id);
    if (outcome.status === 'success' || outcome.reason === 'budget_not_found') {
      // Either way it is gone: drop it and select the (refetched) first budget.
      markGone(target.id);
      void refetchList();
      notify(
        'success',
        'Budget deleted',
        outcome.status === 'success'
          ? `The budget “${target.name}” was deleted.`
          : `The budget “${target.name}” had already been deleted.`,
      );
      return CLOSE_MODAL;
    }
    return handleWriteError(outcome, 'Could not delete the budget', target.id);
  };

  const handleLineSubmit = async (budgetId: number, submit: GpuBudgetLineSubmit): Promise<ModalFeedback> => {
    const outcome =
      submit.mode === 'add'
        ? await mutations.addLine(budgetId, submit.body)
        : await mutations.updateLine(budgetId, submit.line.id, submit.body);
    if (outcome.status !== 'success') {
      return handleWriteError(
        outcome,
        submit.mode === 'add' ? 'Could not add the line' : 'Could not update the line',
        budgetId,
      );
    }
    setBudget(outcome.budget);
    void refetchList();
    const lineId = 'lineId' in outcome ? outcome.lineId : submit.mode === 'edit' ? submit.line.id : null;
    const saved = outcome.budget.lines.find((line) => line.id === lineId);
    const what = saved !== undefined ? lineName(saved) : 'The line';
    notify(
      'success',
      submit.mode === 'add' ? 'Line added' : 'Line updated',
      `${what} — saved in the budget “${outcome.budget.name}”.`,
    );
    return CLOSE_MODAL;
  };

  const handleRemoveLine = async (budgetId: number, line: GpuBudgetLine): Promise<ModalFeedback> => {
    const outcome = await mutations.deleteLine(budgetId, line.id);
    if (outcome.status !== 'success') {
      return handleWriteError(outcome, 'Could not remove the line', budgetId);
    }
    setBudget(outcome.budget);
    void refetchList();
    notify('success', 'Line removed', `${lineName(line)} was removed from the budget “${outcome.budget.name}”.`);
    return CLOSE_MODAL;
  };

  const isRefreshing = list.loading || catalogState.loading || budgetState.loading;
  const handleRefresh = () => {
    if (isRefreshing) {
      return;
    }
    void refetchList();
    void catalogState.refetch();
    if (selectedId !== null) {
      void refetchBudget();
    }
  };

  const impersonationAlert = isImpersonating ? (
    <Alert variant="info" isInline title="Editing as support" className="pf-v6-u-mb-md">
      {BUDGET_IMPERSONATION_NOTICE}
    </Alert>
  ) : null;

  // 1. First entry: the list or the catalog is still loading.
  if ((budgets === null && list.loading) || (catalog === null && catalogState.loading)) {
    return (
      <>
        {impersonationAlert}
        <CenteredSpinner />
      </>
    );
  }

  // 2. The list or the catalog failed (the router's `fail` included): one error state, no toolbar.
  if (budgets === null || catalog === null || candidates === null) {
    const messages = Array.from(
      new Set([budgets === null ? list.error : null, catalog === null ? catalogState.error : null]),
    ).filter((message): message is string => message !== null);
    return (
      <>
        {impersonationAlert}
        <EmptyState
          titleText="Unable to load your budgets"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>
            {messages.length > 0
              ? messages.map((message) => <div key={message}>{message}</div>)
              : 'Could not load your budgets.'}
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                onClick={() => {
                  if (budgets === null) {
                    void refetchList();
                  }
                  if (catalog === null) {
                    void catalogState.refetch();
                  }
                }}
              >
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </>
    );
  }

  const atBudgetLimit = candidates.length >= catalog.limits.max_budgets;
  const newBudgetButton = (label: string) => {
    const button = (
      <Button
        variant="primary"
        icon={<PlusCircleIcon />}
        onClick={() => setModal({ type: 'create' })}
        isAriaDisabled={atBudgetLimit}
      >
        {label}
      </Button>
    );
    return atBudgetLimit ? (
      <Tooltip
        content={`An organisation can keep at most ${formatCount(catalog.limits.max_budgets)} budgets. Delete one to create another.`}
      >
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  const refreshErrors = Array.from(
    new Set([list.error, catalogState.error].filter((message): message is string => message !== null)),
  );

  const renderEditorArea = () => {
    if (budget !== null) {
      return (
        <>
          {budgetState.error !== null && (
            <Alert
              variant="danger"
              isInline
              title="Could not refresh this budget — showing the last loaded version"
              className="pf-v6-u-mb-md"
              actionLinks={<AlertActionLink onClick={() => void refetchBudget()}>Retry</AlertActionLink>}
            >
              {budgetState.error}
            </Alert>
          )}
          <GpuBudgetEditor
            budget={budget}
            catalog={catalog}
            isBusy={mutations.pending}
            onAddLine={(kind) => setModal({ type: 'line', budgetId: budget.id, kind, line: null })}
            onEditLine={(kind, line) => setModal({ type: 'line', budgetId: budget.id, kind, line })}
            onRemoveLine={(line) => setModal({ type: 'remove-line', budgetId: budget.id, line })}
          />
        </>
      );
    }
    // 5. The selected budget failed (other than not-found).
    if (budgetState.error !== null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Could not load this budget"
          actionLinks={
            <AlertActionLink onClick={() => void refetchBudget()} isDisabled={budgetState.loading}>
              Retry
            </AlertActionLink>
          }
        >
          {budgetState.error}
        </Alert>
      );
    }
    // 4. The selected budget is loading.
    return <CenteredSpinner />;
  };

  const renderModal = () => {
    if (modal === null) {
      return null;
    }
    switch (modal.type) {
      case 'create':
        return (
          <GpuBudgetNameModal
            mode="create"
            initialName=""
            nameMaxLength={catalog.limits.name_max_length}
            onSubmit={handleCreate}
            onClose={closeModal}
          />
        );
      case 'rename':
        return (
          <GpuBudgetNameModal
            mode="rename"
            initialName={modal.budget.name}
            nameMaxLength={catalog.limits.name_max_length}
            onSubmit={(name) => handleRename(modal.budget, name)}
            onClose={closeModal}
          />
        );
      case 'delete':
        return (
          <GpuBudgetConfirmModal
            title="Delete budget"
            message={`Delete the budget “${modal.budget.name}”? Its lines are deleted with it.`}
            confirmLabel="Delete"
            onConfirm={() => handleDelete(modal.budget)}
            onClose={closeModal}
          />
        );
      case 'line':
        return (
          <GpuBudgetLineModal
            kind={modal.kind}
            line={modal.line}
            onSubmit={(submit) => handleLineSubmit(modal.budgetId, submit)}
            onClose={closeModal}
          />
        );
      case 'remove-line': {
        const unit = catalogKind(catalog, modal.line.kind)?.quantity.unit ?? null;
        return (
          <GpuBudgetConfirmModal
            title="Remove line"
            message={`Remove this line (${lineName(modal.line)}, ${formatQuantity(unit, modal.line.quantity)}) from the budget?`}
            confirmLabel="Remove"
            onConfirm={() => handleRemoveLine(modal.budgetId, modal.line)}
            onClose={closeModal}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      {impersonationAlert}
      {refreshErrors.length > 0 && (
        <Alert
          variant="warning"
          isInline
          isPlain
          title="Could not refresh your budgets — showing the last loaded version"
          className="pf-v6-u-mb-md"
          actionLinks={<AlertActionLink onClick={handleRefresh}>Retry</AlertActionLink>}
        >
          {refreshErrors.join(' ')}
        </Alert>
      )}

      {candidates.length === 0 ? (
        <>
          <GpuBudgetWarningStack budget={null} catalog={catalog} />
          <EmptyState titleText={BUDGET_EMPTY_TITLE} icon={CalculatorIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>{BUDGET_EMPTY_BODY}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{newBudgetButton('Create a budget')}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </>
      ) : (
        <>
          <Toolbar>
            <ToolbarContent>
              <ToolbarItem>
                <Select
                  isOpen={isPickerOpen}
                  selected={selectedId ?? undefined}
                  onSelect={(_event, value) => {
                    if (typeof value === 'number') {
                      selectBudget(value);
                    }
                    setIsPickerOpen(false);
                  }}
                  onOpenChange={(open) => setIsPickerOpen(open)}
                  toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle
                      ref={toggleRef}
                      onClick={() => setIsPickerOpen(!isPickerOpen)}
                      isExpanded={isPickerOpen}
                      aria-label="Choose the budget to edit"
                      style={{ minWidth: '16rem' }}
                    >
                      {selectedItem?.name ?? 'Select a budget'}
                    </MenuToggle>
                  )}
                  shouldFocusToggleOnSelect
                >
                  <SelectList aria-label="Budgets">
                    {candidates.map((item) => (
                      <SelectOption
                        key={item.id}
                        value={item.id}
                        isSelected={item.id === selectedId}
                        description={budgetOptionDescription(item)}
                      >
                        {item.name}
                      </SelectOption>
                    ))}
                  </SelectList>
                </Select>
              </ToolbarItem>
              <ToolbarGroup variant="action-group">
                <ToolbarItem>{newBudgetButton('New budget')}</ToolbarItem>
                <ToolbarItem>
                  <Button
                    variant="secondary"
                    icon={<PencilAltIcon />}
                    onClick={() => selectedItem !== null && setModal({ type: 'rename', budget: selectedItem })}
                    isDisabled={selectedItem === null}
                  >
                    Rename
                  </Button>
                </ToolbarItem>
                <ToolbarItem>
                  <Button
                    variant="secondary"
                    isDanger
                    icon={<TrashIcon />}
                    onClick={() => selectedItem !== null && setModal({ type: 'delete', budget: selectedItem })}
                    isDisabled={selectedItem === null}
                  >
                    Delete
                  </Button>
                </ToolbarItem>
              </ToolbarGroup>
              <ToolbarItem align={{ default: 'alignEnd' }}>
                <Button
                  variant="secondary"
                  icon={<SyncAltIcon />}
                  isLoading={isRefreshing}
                  spinnerAriaValueText="Refreshing your budgets"
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              </ToolbarItem>
            </ToolbarContent>
          </Toolbar>
          <GpuBudgetWarningStack budget={budget} catalog={catalog} />
          {renderEditorArea()}
        </>
      )}

      <Content component="p" className="pf-v6-u-mt-md pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
        {BUDGET_ESTIMATE_NOTE}
      </Content>

      {renderModal()}
    </>
  );
}

export default GpuBudgetTab;
